import React from "react";
import { comparatorBy } from "diagnostics-lib/comparator";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import t from "../../../../../../../../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { useTranslations } from "../../../../../../../../../../api/useTranslations";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import { useImpactChartType } from "../../../../../../../../Diagnosis/FailureEvolution/hooks/useImpactChartType";
import type { Quantity } from "../../../../../../../../../../api/useQuantities";
import type { Event } from "../../../../../../../../../../api/useEvents";
import { getEventScope } from "../../../../../../../../../../domain/event";

const useChartOptionMenu = createGlobalState({
  storage: "session",
  key: "useChartOptionMenu",
  type: io.string,
  scoped: true,
});

export const FAULT_AMPLITUDE_CHART = "FAULT_AMPLITUDE_CHART";
export const FAULT_IMPACT_CHART = "FAULT_IMPACT_CHART";

export const useFaultGraphOptions = ({
  event,
  quantities,
}: {
  event: Event;
  quantities: Quantity[];
}) => {
  /** Common */
  const translations = useTranslations();

  const performanceIndicator = React.useMemo(
    () => quantities.filter(withKeyNotNull("qoiOrder")).sort(comparatorBy("qoiOrder")),
    [quantities],
  );

  /** Menu */
  const [graphType, setGraphType] = useChartOptionMenu({
    scope: getEventScope(event),
    defaultValue: performanceIndicator.length > 0 ? FAULT_IMPACT_CHART : FAULT_AMPLITUDE_CHART,
  });
  const graphTypeOptions = React.useMemo(() => {
    const impactChartOption =
      performanceIndicator.length > 0
        ? [
            {
              label: t("project.fault_event.fault.evolution.overview.header.option.impact"),
              value: FAULT_IMPACT_CHART,
            },
          ]
        : [];
    return [
      {
        label: t("project.fault_event.fault.evolution.overview.header.option.amplitude"),
        value: FAULT_AMPLITUDE_CHART,
      },
      ...impactChartOption,
    ];
  }, [performanceIndicator]);

  /** Selector */
  const [impactSelector_, setImpactSelector] = useImpactChartType({
    scope: getEventScope(event),
    defaultValue: performanceIndicator[0]?.id ?? "",
  });
  const impactsSelectorOptions = React.useMemo(
    () =>
      performanceIndicator.map(q => ({
        label: t("diagnosis.overview.header.option", q),
        value: String(q.id),
      })) as NonEmptyArray<{ label: string; value: string }>,
    [performanceIndicator],
  );
  const impactSelector = React.useMemo(() => {
    const is = impactsSelectorOptions.find(f => f.value === impactSelector_);
    if (is === undefined) {
      return impactsSelectorOptions[0]?.value ?? "";
    }
    return is.value;
  }, [impactsSelectorOptions, impactSelector_]);

  const impactSelectorDescriptions = React.useMemo(
    () => [
      {
        label: t("project.fault_event.fault.evolution.overview.header.option.amplitude"),
        value: FAULT_AMPLITUDE_CHART,
        description: t(
          "project.fault_event.fault.evolution.overview.header.option.amplitude.description",
        ),
      },
      {
        label: t("diagnosis.overview.header.option.qoi"),
        value: "QOI",
        description: t("diagnosis.overview.header.option.qoi.description", { translations }),
      },
    ],
    [translations],
  );

  const graphOptionMenu = React.useMemo(
    () => ({ graphType, graphTypeOptions, setGraphType }),
    [graphType, graphTypeOptions, setGraphType],
  );

  const graphOptionSelector = React.useMemo(
    () => ({
      impactSelector,
      setImpactSelector,
      impactsSelectorOptions,
      impactSelectorDescriptions,
    }),
    [impactSelector, setImpactSelector, impactsSelectorOptions, impactSelectorDescriptions],
  );

  return {
    graphOptionMenu,
    graphOptionSelector,
  };
};
